// TMOS Education Module Detail Page
// Date: 2025-11-16
// Purpose: Display detailed content of an education module

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../state/language/LanguageProvider';
import { useAuth } from '../../state/auth/AuthProvider';
import educationService, { EducationModule } from '../../services/educationService';
import {
  ArrowLeftIcon,
  BookOpenIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid';

export const EducationModuleDetailPage: React.FC = () => {
  const { moduleId } = useParams<{ moduleId: string }>();
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Debug: Log the moduleId from route params
  useEffect(() => {
    console.log('EducationModuleDetailPage - moduleId from useParams:', moduleId);
    console.log('EducationModuleDetailPage - current URL:', window.location.pathname);
  }, [moduleId]);

  const [module, setModule] = useState<EducationModule | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentSection, setCurrentSection] = useState(0);
  const [progress, setProgress] = useState(0);
  const [savingProgress, setSavingProgress] = useState(false);

  useEffect(() => {
    // Check if moduleId is valid before proceeding
    if (!moduleId || moduleId === ':moduleId' || moduleId.startsWith(':')) {
      console.error('Invalid moduleId detected:', moduleId);
      setError('Invalid module ID. Please select a module from the education center.');
      setLoading(false);
      return;
    }

    if (moduleId && user?.id) {
      loadModule();
    } else if (!user?.id) {
      setError('User not authenticated');
      setLoading(false);
    }
  }, [moduleId, user]);

  const loadModule = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!moduleId || moduleId === ':moduleId' || !user?.id) {
        setError('Module ID or user ID missing');
        console.error('Invalid moduleId:', moduleId);
        return;
      }

      console.log('Loading module with ID:', moduleId);
      const moduleData = await educationService.getModuleContent(moduleId, user.id);
      
      if (!moduleData) {
        setError('Module not found');
        return;
      }

      setModule(moduleData);
      setProgress(moduleData.progress_percentage || 0);
      
      // If user has progress, start from where they left off
      if (moduleData.progress_percentage && moduleData.progress_percentage > 0) {
        const sections = moduleData.content?.sections || [];
        const progressPercent = moduleData.progress_percentage;
        const estimatedSection = Math.floor((progressPercent / 100) * sections.length);
        setCurrentSection(Math.min(estimatedSection, sections.length - 1));
      }
    } catch (err: any) {
      console.error('Error loading module:', err);
      setError(err.response?.data?.message || 'Failed to load module');
    } finally {
      setLoading(false);
    }
  };

  const updateProgress = async (newProgress: number, completed: boolean = false) => {
    if (!moduleId || !user?.id || savingProgress) return;

    try {
      setSavingProgress(true);
      await educationService.updateModuleProgress(moduleId, user.id, newProgress, completed);
      setProgress(newProgress);
    } catch (err) {
      console.error('Error updating progress:', err);
    } finally {
      setSavingProgress(false);
    }
  };

  const handleSectionChange = (index: number) => {
    setCurrentSection(index);
    
    if (!module?.content?.sections) return;
    
    const sections = module.content.sections;
    const newProgress = Math.round(((index + 1) / sections.length) * 100);
    const completed = index === sections.length - 1 && newProgress >= 100;
    
    updateProgress(newProgress, completed);
  };

  const handleNext = () => {
    if (!module?.content?.sections) return;
    
    if (currentSection < module.content.sections.length - 1) {
      handleSectionChange(currentSection + 1);
    }
  };

  const handlePrevious = () => {
    if (currentSection > 0) {
      handleSectionChange(currentSection - 1);
    }
  };

  const handleComplete = () => {
    if (!module?.content?.sections) return;
    
    updateProgress(100, true);
  };

  const formatContent = (content: string) => {
    // Convert markdown-style formatting to HTML
    const formatted = content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\n/g, '<br />');
    
    return { __html: formatted };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-secondary-bg)] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--color-info)] mx-auto"></div>
          <p className="mt-4 text-[var(--color-secondary-text)]">Loading module...</p>
        </div>
      </div>
    );
  }

  if (error || !module) {
    return (
      <div className="min-h-screen bg-[var(--color-secondary-bg)] flex items-center justify-center">
        <div className="text-center">
          <XCircleIcon className="h-16 w-16 text-[var(--color-error)] mx-auto mb-4" />
          <p className="text-[var(--color-error)] mb-4">{error || 'Module not found'}</p>
          <button
            onClick={() => navigate('/education')}
            className="px-4 py-2 bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] rounded-md hover:opacity-90"
          >
            {t('education.backToModules') || 'Back to Modules'}
          </button>
        </div>
      </div>
    );
  }

  const sections = module.content?.sections || [];
  const currentSectionData = sections[currentSection];
  const isCompleted = module.completed_at !== null && module.completed_at !== undefined;
  const isLastSection = currentSection === sections.length - 1;

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/education')}
            className="flex items-center space-x-2 text-[var(--color-secondary-text)] hover:text-[var(--color-surface-text)] mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            <span>{t('education.backToModules') || 'Back to Modules'}</span>
          </button>

          <div className="bg-[var(--color-surface-bg)] rounded-lg shadow-sm border border-[var(--color-border)] p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <BookOpenIcon className="h-8 w-8 text-[var(--color-info)]" />
                  <h1 className="text-2xl font-bold text-[var(--color-surface-text)]">{module.title}</h1>
                </div>
                {isCompleted && (
                  <div className="flex items-center space-x-1 text-[var(--color-success)] text-sm mb-2">
                    <CheckCircleIconSolid className="h-5 w-5" />
                    <span>{t('education.completed') || 'Completed'}</span>
                  </div>
                )}
                <p className="text-[var(--color-secondary-text)]">{module.description}</p>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex items-center justify-between text-sm text-[var(--color-secondary-text)] mb-2">
                <span>{t('education.progress') || 'Progress'}</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div className="w-full bg-[var(--color-border)] rounded-full h-3">
                <div
                  className={`h-3 rounded-full transition-all ${
                    progress === 100 ? 'bg-[var(--color-success)]' : progress >= 50 ? 'bg-[var(--color-info)]' : 'bg-[var(--color-warning)]'
                  }`}
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            {/* Section Navigation */}
            {sections.length > 1 && (
              <div className="flex space-x-2 overflow-x-auto pb-2">
                {sections.map((section, index) => (
                  <button
                    key={index}
                    onClick={() => handleSectionChange(index)}
                    className={`px-3 py-1 rounded-md text-sm whitespace-nowrap transition-colors ${
                      index === currentSection
                        ? 'bg-[var(--color-info)] text-[var(--color-accent-text)]'
                        : index < currentSection
                        ? 'bg-[var(--color-success)]/10 text-[var(--color-success)] hover:bg-[var(--color-success)]/20'
                        : 'bg-[var(--color-border)] text-[var(--color-secondary-text)] hover:bg-[var(--color-secondary-bg)]'
                    }`}
                  >
                    {index + 1}. {section.title.substring(0, 20)}
                    {index < currentSection && (
                      <CheckCircleIconSolid className="h-4 w-4 inline-block ml-1" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        {currentSectionData && (
          <div className="bg-[var(--color-surface-bg)] rounded-lg shadow-sm border border-[var(--color-border)] p-8 mb-6">
            <h2 className="text-2xl font-bold text-[var(--color-surface-text)] mb-6">
              {currentSectionData.title}
            </h2>
            <div
              className="prose prose-lg max-w-none text-[var(--color-surface-text)]"
              dangerouslySetInnerHTML={formatContent(currentSectionData.content)}
            />
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between bg-[var(--color-surface-bg)] rounded-lg shadow-sm border border-[var(--color-border)] p-4">
          <button
            onClick={handlePrevious}
            disabled={currentSection === 0}
            className={`px-6 py-2 rounded-md font-medium transition-colors ${
              currentSection === 0
                ? 'bg-[var(--color-border)] text-[var(--color-secondary-text)] opacity-50 cursor-not-allowed'
                : 'bg-[var(--color-secondary-bg)] text-[var(--color-surface-text)] hover:bg-[var(--color-border)]'
            }`}
          >
            {t('education.previous') || 'Previous'}
          </button>

          <div className="text-sm text-[var(--color-secondary-text)]">
            {t('education.section') || 'Section'} {currentSection + 1} {t('education.of') || 'of'} {sections.length}
          </div>

          {isLastSection ? (
            <button
              onClick={handleComplete}
              disabled={isCompleted || savingProgress}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${
                isCompleted || savingProgress
                  ? 'bg-[var(--color-success)]/10 text-[var(--color-success)] opacity-50 cursor-not-allowed'
                  : 'bg-[var(--color-success)] text-[var(--color-accent-text)] hover:opacity-90'
              }`}
            >
              {isCompleted
                ? t('education.completed') || 'Completed'
                : t('education.markComplete') || 'Mark as Complete'}
            </button>
          ) : (
            <button
              onClick={handleNext}
              className="px-6 py-2 bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] rounded-md font-medium hover:opacity-90 transition-colors"
            >
              {t('education.next') || 'Next'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

